import React from 'react';

const WhatYoullDiscoverSection = () => (
  <div className="py-16 bg-gradient-to-br from-slate-50 to-gray-100 dark:from-gray-800 dark:to-gray-900">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="text-center mb-16">
        <h2 className="text-4xl font-bold text-gray-900 dark:text-white mb-6">
          What You'll Discover
        </h2>
        <p className="text-lg text-gray-600 dark:text-gray-400 max-w-3xl mx-auto">
          Our comprehensive energy assessment reveals hidden opportunities and provides
          actionable insights to reduce your energy costs immediately.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
        {/* Left Column - Benefits List */}
        <div className="space-y-8">
          <div className="flex items-start gap-4">
            <div className="flex-shrink-0 w-12 h-12 bg-emerald-100 dark:bg-emerald-900/30 rounded-lg flex items-center justify-center">
              <svg
                className="w-6 h-6 text-emerald-600 dark:text-emerald-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1"
                />
              </svg>
            </div>
            <div>
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                Immediate Cost Savings Opportunities
              </h3>
              <p className="text-gray-600 dark:text-gray-400">
                Identify specific areas where you can reduce energy costs by 15-30% within the
                first 12 months, with detailed implementation costs and payback periods.
              </p>
            </div>
          </div>

          <div className="flex items-start gap-4">
            <div className="flex-shrink-0 w-12 h-12 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center">
              <svg
                className="w-6 h-6 text-blue-600 dark:text-blue-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                />
              </svg>
            </div>
            <div>
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                Benchmarked Performance Analysis
              </h3>
              <p className="text-gray-600 dark:text-gray-400">
                Compare your energy performance against industry leaders and discover how much
                more efficient your facility could become with proven technologies.
              </p>
            </div>
          </div>

          <div className="flex items-start gap-4">
            <div className="flex-shrink-0 w-12 h-12 bg-purple-100 dark:bg-purple-900/30 rounded-lg flex items-center justify-center">
              <svg
                className="w-6 h-6 text-purple-600 dark:text-purple-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M13 10V3L4 14h7v7l9-11h-7z"
                />
              </svg>
            </div>
            <div>
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                Equipment Optimization Recommendations
              </h3>
              <p className="text-gray-600 dark:text-gray-400">
                Get specific recommendations for your HVAC, lighting, compressed air, and process
                equipment with exact model suggestions and energy impact projections.
              </p>
            </div>
          </div>

          <div className="flex items-start gap-4">
            <div className="flex-shrink-0 w-12 h-12 bg-emerald-100 dark:bg-emerald-900/30 rounded-lg flex items-center justify-center">
              <svg
                className="w-6 h-6 text-emerald-600 dark:text-emerald-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M8 7V3a2 2 0 012-2h4a2 2 0 012 2v4m-6 9l6-6m0 0l6 6m-6-6v12"
                />
              </svg>
            </div>
            <div>
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                Implementation Roadmap & Funding Options
              </h3>
              <p className="text-gray-600 dark:text-gray-400">
                Receive a prioritized action plan with timeline, responsible parties, and
                connections to trusted implementation partners and available government
                incentives.
              </p>
            </div>
          </div>
        </div>

        {/* Right Column - Results Preview */}
        <div className="lg:justify-self-end">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-8 border border-gray-200 dark:border-gray-700">
            <div className="text-center mb-8">
              <h4 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                Sample Assessment Results
              </h4>
              <p className="text-gray-600 dark:text-gray-400">
                Food & Beverage Manufacturing Facility
              </p>
            </div>

            <div className="space-y-6">
              <div className="flex justify-between items-center p-4 bg-emerald-50 dark:bg-emerald-900/30 rounded-lg">
                <div>
                  <div className="text-sm text-emerald-700 dark:text-emerald-300 font-medium">
                    Annual Savings Potential
                  </div>
                  <div className="text-2xl font-bold text-emerald-600 dark:text-emerald-400">
                    $176,825
                  </div>
                </div>
                <div className="text-emerald-500 dark:text-emerald-400">
                  <svg
                    className="w-8 h-8"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"
                    />
                  </svg>
                </div>
              </div>

              <div className="flex justify-between items-center p-4 bg-blue-50 dark:bg-blue-900/30 rounded-lg">
                <div>
                  <div className="text-sm text-blue-700 dark:text-blue-300 font-medium">
                    Energy Cost Reduction
                  </div>
                  <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">28.3%</div>
                </div>
                <div className="text-blue-500 dark:text-blue-400">
                  <svg
                    className="w-8 h-8"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M13 10V3L4 14h7v7l9-11h-7z"
                    />
                  </svg>
                </div>
              </div>

              <div className="flex justify-between items-center p-4 bg-purple-50 dark:bg-purple-900/30 rounded-lg">
                <div>
                  <div className="text-sm text-purple-700 dark:text-purple-300 font-medium">
                    Implementation Opportunities
                  </div>
                  <div className="text-2xl font-bold text-purple-600 dark:text-purple-400">
                    15 RECCs
                  </div>
                </div>
                <div className="text-purple-500 dark:text-purple-400">
                  <svg
                    className="w-8 h-8"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                </div>
              </div>

              <div className="flex justify-between items-center p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                <div>
                  <div className="text-sm text-gray-700 dark:text-gray-300 font-medium">
                    Average Payback Period
                  </div>
                  <div className="text-2xl font-bold text-gray-600 dark:text-gray-300">
                    1.8 years
                  </div>
                </div>
                <div className="text-gray-500 dark:text-gray-400">
                  <svg
                    className="w-8 h-8"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                </div>
              </div>
            </div>

            <div className="mt-8 pt-6 border-t border-gray-200 dark:border-gray-700">
              <p className="text-sm text-gray-600 dark:text-gray-400 text-center">
                Results based on real IAC assessment data from similar facilities
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
);

export default WhatYoullDiscoverSection;


